/**
 * Los 11 parámetros canónicos del rig facial.
 *
 * **Este archivo es la casa canónica del contrato de parámetros del
 * motor** (`docs/04_ARQUITECTURA.md` §6.5). Es deliberadamente distinto
 * del *contrato del asset* — lo que el artista entrega, en blend shapes
 * ARKit y huesos — que vive en `docs/06_ESTANDAR_RIG.md`. La traducción
 * entre ambos es responsabilidad del adapter y está tabulada en
 * `docs/07_BLENDSHAPES_ARKIT.md` §4.
 *
 * El runtime gráfico concreto lo implementa el `RigAdapter` de cada app
 * (`ThreeRigAdapter` en el MVP). Este paquete no conoce el runtime —
 * sólo define el vocabulario abstracto que ambos lados usan.
 *
 * **Lateralidad: punto de vista del avatar.** `_izq` es el lado
 * izquierdo *del paciente*, el que el alumno ve a su derecha. Coincide
 * con la convención de ARKit (`07` §2) y por lo tanto la traducción es
 * directa: `_izq → *Left`. Verificado en la calibración de F0.6 con
 * `mirada_x`, el único parámetro donde la diferencia se nota — las
 * expresiones del catálogo son simétricas salvo la mirada de `AVERSION`.
 *
 * Se deja anotado porque el estándar de rig 2D anterior definía lo
 * contrario ("desde el punto de vista del observador, no del
 * paciente"). Esa definición murió con la reescritura de
 * `06_ESTANDAR_RIG.md` a v2.0; si algún documento vuelve a fijarla, es
 * ésta la que rige.
 */
export interface RigParams {
  /**
   * Ceja izquierda, extremo **externo**. `-1` = completamente bajada
   * (enojo, concentración), `0` = neutra, `1` = completamente subida
   * (sorpresa, atención).
   *
   * Es el eje del músculo que baja y sube la ceja entera desde afuera.
   * El extremo interno tiene su propio parámetro: ver `ceja_interna`.
   */
  ceja_izq: number;
  /** Ceja derecha, extremo externo. Idem espejado. */
  ceja_der: number;
  /**
   * Elevación del extremo **interno** de ambas cejas — el centro, sobre
   * la nariz. `0` = neutro, `1` = completamente elevado. Sin lado: es un
   * solo músculo bilateral.
   *
   * Es *el* canal de la tristeza (las cejas en "^") y componente del
   * miedo y la preocupación. Existe aparte de `ceja_izq`/`ceja_der`
   * porque en una cara real son músculos **distintos y opuestos**:
   * bajar la ceja es enojo, subir su parte interna es tristeza. Un solo
   * eje con signo no puede expresar las dos, y la calibración de F0.6
   * lo demostró — `TRISTE` con la ceja baja daba una boca triste bajo
   * cejas enojadas.
   *
   * Combinado con `ceja_*` positivo da la ceja completamente alzada de
   * la sorpresa; solo, da la ceja triste.
   */
  ceja_interna: number;
  /**
   * Párpado izquierdo. `0` = ojo cerrado, `1` = ojo abierto en apertura
   * cómoda neutral. **Neutro es 1.0**, no 0 — a diferencia de `ceja_*` y
   * `comisura_*`, donde el neutro es 0 y el signo indica dirección.
   *
   * No hay headroom para "más abierto que cómodo": el ojo muy abierto de
   * la sorpresa extrema necesitaría un rango por encima del neutro que
   * este parámetro no tiene. Ver `07_BLENDSHAPES_ARKIT.md` §4.
   */
  parpado_izq: number;
  /** Párpado derecho. Idem espejado. */
  parpado_der: number;
  /**
   * Comisura izquierda. `-1` = totalmente bajada (tristeza), `0` =
   * neutra, `1` = totalmente subida (alegría).
   */
  comisura_izq: number;
  /** Comisura derecha. Idem espejado. */
  comisura_der: number;
  /**
   * Tensión mandibular. `0` = mandíbula relajada, `1` = apretada
   * (dolor, ira contenida).
   */
  tension_mandib: number;
  /**
   * Mirada horizontal. `-1` = el paciente mira a **su** izquierda, `0` =
   * al frente, `1` = a su derecha. Ver la nota de lateralidad de
   * `RigParams`: es el único parámetro donde la convención se nota.
   */
  mirada_x: number;
  /** Mirada vertical. `-1` = mira hacia abajo, `0` = al frente, `1` = hacia arriba. */
  mirada_y: number;
  /**
   * Modulador global de intensidad de la expresión: `0..1`. Multiplica
   * el efecto del resto de los parámetros. **Neutro es 1.0.**
   */
  intensidad: number;
}

/**
 * Configuración por expresión. Cada entrada en `EXPRESIONES_CONFIG` es
 * un `ExpresionConfig`: los parámetros faciales son opcionales (caen a
 * `NEUTRAL_PARAMS` al resolver) y `intensidad` también es opcional
 * (cae a `1.0`).
 *
 * Equivalente a `Partial<RigParams>` pero declarado explícito para
 * matchear `04_ARQUITECTURA.md` §6.4.
 */
export type ExpresionConfig = Partial<Omit<RigParams, 'intensidad'>> & {
  intensidad?: number;
};

/**
 * Pose neutra del rig: el punto de partida y el default cuando una
 * `ExpresionConfig` no especifica un parámetro. Usado por el motor al
 * resolver `apply()`.
 *
 * Decisión menor F0.3: los párpados están en `1.0` (abiertos), no en `0`.
 * El doc no fija este default, pero un rostro relajado tiene los ojos
 * abiertos. `intensidad: 1.0` para que `NEUTRAL` sin override muestre
 * la pose plena (no atenuada).
 */
export const NEUTRAL_PARAMS: RigParams = {
  ceja_izq: 0,
  ceja_der: 0,
  ceja_interna: 0,
  parpado_izq: 1,
  parpado_der: 1,
  comisura_izq: 0,
  comisura_der: 0,
  tension_mandib: 0,
  mirada_x: 0,
  mirada_y: 0,
  intensidad: 1,
};

/** Default de duración de transición entre poses, en ms. */
export const DEFAULT_TRANSITION_MS = 800;
